import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { List } from '@7f-ui/components/list/list';
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { textFieldVariants } from '@7f-ui/components/text-field/text-field';
import { Panel } from '@feat/feat-search/panel';
import { ComboboxInput, ComboboxOptions, Field, Input, Label } from '@headlessui/react';
import { MapPin } from '@phosphor-icons/react';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { Fragment, forwardRef } from 'react';
import type { ControllerRenderProps } from 'react-hook-form';
import type { SearchResult } from '../src/lib/use-geo-search';
import { FallbackList } from './fallback-list';
import { LocationCombobox } from './location-combobox/location-combobox';
import { OptionsList } from './location-combobox/options-list';
import type { FormValues } from './search-field';
type InputProps = Omit<ControllerRenderProps<FormValues>, 'value' | 'ref' | 'onChange'>;
type LocationFieldProps = {
  value: SearchResult | null;
  inputProps: InputProps & {
    placeholder?: string;
  };
  label: string;
  error?: string;
  isActive: boolean;
  onChange: (value: SearchResult | null) => void;
  onClose?: () => void;
  onReset?: () => void;
  onInputChange?: (value: string) => void;
  onLabelClick?: () => void;
  onFocus?: () => void;
};
const LocationField = forwardRef<HTMLElement, LocationFieldProps>(function LocationField(t0, ref) {
  const $ = _c(21);
  const {
    value,
    inputProps,
    error,
    label: fieldLabel,
    isActive,
    onChange,
    onReset,
    onClose,
    onInputChange,
    onLabelClick,
    onFocus
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = textFieldVariants({
      inputSpacing: false
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    input,
    label
  } = t1;
  let t2;
  if ($[1] !== fieldLabel || $[2] !== inputProps || $[3] !== isActive || $[4] !== onFocus || $[5] !== onInputChange || $[6] !== onLabelClick || $[7] !== ref || $[8] !== value) {
    t2 = t3 => {
      const {
        onChange: onInputValueChange,
        renderResetButton,
        displayValue,
        onKeyDown,
        onBlur
      } = t3;
      return <Field as={Fragment}><Label className="location-label grid size-12 cursor-pointer place-content-center text-secondary-text" onClick={onLabelClick}><MapPin size={24} weight="bold" /></Label><div className={cx("location-input relative flex flex-1 pr-4", {
          "pr-0": value
        })}><Label className={label()}>{fieldLabel}</Label><ComboboxInput as={Fragment} displayValue={displayValue}><Input {...inputProps} ref={ref} onChange={event => {
              onInputValueChange?.(event);
              onInputChange?.(event.target.value);
            }} className={cx(input(), "pt-5 pb-1")} onFocus={onFocus} onKeyDown={onKeyDown} onBlur={onBlur} data-testid={shopTestId("Search.location-input")} /></ComboboxInput>{renderResetButton?.({
            tabIndex: isActive ? 0 : -1
          })}</div></Field>;
    };
    $[1] = fieldLabel;
    $[2] = inputProps;
    $[3] = isActive;
    $[4] = onFocus;
    $[5] = onInputChange;
    $[6] = onLabelClick;
    $[7] = ref;
    $[8] = value;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== error || $[11] !== isActive || $[12] !== onChange) {
    t3 = t4 => {
      const {
        options,
        savedOptions,
        isLoading,
        isOpen
      } = t4;
      return <Panel className={cx("left-0 grid max-w-112 gap-4", {
        hidden: !isActive
      })}>{error && !isOpen && <MessageBox type="error">{error}</MessageBox>}{isOpen && <ComboboxOptions as={List} static={true} className="max-h-96 overflow-scroll">{options && <OptionsList isLoading={isLoading} options={options} />}</ComboboxOptions>}{!isOpen && <FallbackList searchResults={savedOptions.slice(0, 2)} onLocationClick={onChange} />}</Panel>;
    };
    $[10] = error;
    $[11] = isActive;
    $[12] = onChange;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== onChange || $[15] !== onClose || $[16] !== onReset || $[17] !== t2 || $[18] !== t3 || $[19] !== value) {
    t4 = <LocationCombobox value={value} onChange={onChange} onClose={onClose} onReset={onReset} renderInput={t2}>{t3}</LocationCombobox>;
    $[14] = onChange;
    $[15] = onClose;
    $[16] = onReset;
    $[17] = t2;
    $[18] = t3;
    $[19] = value;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  return t4;
});
LocationField.displayName = 'LocationField';
export { LocationField };